import { MotifTooltip } from "@ey-xd/motif-react";
import { truncateString } from "../../utils/common";
import "./BundleCard.scss";

type SkuDetailsType = {
  SkuId: string;
  ProductId: string;
  ProductSkuNum: number;
  Name: string;
  Description: string;
  CostMacroCategoryCd: string;
  CostMetricCd: string;
  CostMetricCost: number;
  CurrencyCd: string;
  CostIntervalCd: string;
  CostIntervalMin: number;
  CostIntervalMax: number;
  IsAutoProvisioned: boolean;
  IsReturnable: boolean;
  StatusCd: string;
  CreatedUser: string;
  UpdatedUser: string;
  CreatedDtm: string;
  UpdatedDtm: string;
  OrderQuantity: number;
  OrderInterval: number;
  SkuRelation: {
    SkuId: string;
    ProductId: string;
    ProductSkuNum: number;
    Name: string;
    Description: string;
    CostMacroCategoryCd: string;
    CostMetricCd: string;
    CostMetricCost: number;
    CurrencyCd: string;
    CostIntervalCd: string;
    CostIntervalMin: number;
    CostIntervalMax: number;
    IsAutoProvisioned: boolean;
    IsReturnable: boolean;
    StatusCd: string;
    CreatedUser: string;
    UpdatedUser: string;
    CreatedDtm: string;
    UpdatedDtm: string;
    OrderQuantity?: number | null; // Optional for relations
    OrderInterval?: number | null; // Optional for relations
  }[];
};

interface BundleCardProps {
  productName: string;
  skuDetails: any[];
  order?: any;
}

const BundleCard = ({ productName, skuDetails, order }: BundleCardProps) => {
  const getCostInterval = (costInterval: string) => {
    if (costInterval == "month") {
      return "Per month";
    }
    if (costInterval == "onetime") {
      return "One time";
    }
    if (costInterval == "week") {
      return "Per week";
    }
  };

  const getDurationType = (costInterval: string) => {
    if (costInterval == "month") {
      return "Months";
    }
    if (costInterval == "onetime") {
      return "One time";
    }
    if (costInterval == "week") {
      return "Weeks";
    }
  };

  const getDuration = (sku: SkuDetailsType) => {
    if (sku.CostIntervalCd == "onetime") {
      return (
        <>
          <div>--</div>
          <div className="light-color">Not Applicable</div>
        </>
      );
    } else {
      return (
        <>
          <div>{sku.OrderInterval}</div>
          <div className="light-color">
            {getDurationType(sku.CostIntervalCd)}
          </div>
        </>
      );
    }
  };

  const calculateTotalCost = (sku: any) => {
    const quantity = sku.OrderQuantity || 0;
    const cost = sku.CostMetricCost || 0;
    const duration = sku.OrderInterval || 0;

    const totalCost = quantity * cost * duration;
    return totalCost.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
  };

  const getTotalOrderAmount = (order: any) => {
    let totalAmount = 0;
    if (!order || !order.SkuDetails || order.SkuDetails.length === 0) {
      return totalAmount.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      });
    } else {
      order.SkuDetails.forEach((sku: any) => {
        totalAmount += getTotalOrderAmountRow(sku);
      });
      return totalAmount.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      });
    }
  };

  const getTotalOrderAmountRow = (sku: any) => {
    if (!sku || sku === null) {
      return 0;
    }
    let totalAmount = 0;
    const quantity = sku.OrderQuantity || 0;
    const cost = sku.CostMetricCost || 0;
    const duration = sku.OrderInterval || 0;
    totalAmount += cost * quantity * duration;
    if (sku.SkuRelation && sku.SkuRelation.length > 0) {
      sku.SkuRelation.forEach((relation: any) => {
        const quantity = relation.OrderQuantity || 0;
        const cost = relation.CostMetricCost || 0;
        const duration = relation.OrderInterval || 0;
        totalAmount += cost * quantity * duration;
      });
    }
    return totalAmount;
  };

  return (
    <>
      {skuDetails.map((sku: any, index: number) => {
        if (index === 0) {
          return (
            <>
              <tr key={sku.SkuId} className="parent-sku-row">
                <td colSpan={4}>
                  <div className="parent-sku">
                    <div className="title">{productName}</div>
                  </div>
                </td>
                <td colSpan={1}>
                  <div className="parent-sku">
                    <div className="total">{getTotalOrderAmount(order)}</div>
                  </div>
                </td>
              </tr>
              <tr className="divider-row">
                <td colSpan={5} className="divider">
                  <hr />
                </td>
              </tr>
              {sku.SkuRelation.length === 0 ? (
                <>
                  <tr key={index}>
                    <td className="sku-cell">
                      <div className="sku-title">
                        <MotifTooltip
                          contentClassName="tooltip-content"
                          allowHover
                          allowClick={false}
                          id={`tooltip`}
                          placement="auto"
                          trigger={
                            <span aria-label="Tooltip">
                              {truncateString(sku.Name, 25)}
                            </span>
                          }
                          usePortal
                          variant="default"
                          hide={sku.Name.length <= 25}
                        >
                          {sku.Name}
                        </MotifTooltip>
                      </div>
                      <div className="sku-number">{sku.SkuId}</div>
                    </td>
                    <td className="qty-td">
                      <div className="table-cell">
                        {sku.CostIntervalCd == "onetime" ? (
                          <span>01</span>
                        ) : (
                          <span>{sku.OrderQuantity}</span>
                        )}
                      </div>
                    </td>
                    <td className="unit-cost-td">
                      <div>
                        {sku.CostMetricCost.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 2,
                        })}
                      </div>
                      <div className="light-color">
                        {getCostInterval(sku.CostIntervalCd)}
                      </div>
                    </td>

                    <td className="duration-td">
                      <div>{getDuration(sku)}</div>
                    </td>
                    <td className="subtotal-td">{calculateTotalCost(sku)}</td>
                  </tr>
                </>
              ) : (
                <>
                  <tr key={index} style={{ verticalAlign: "baseline" }}>
                    <td className="sku-cell">
                      <ul className="tree">
                        <li className="tree-trigger">
                          <div className="table-cell">
                            <div>
                              <MotifTooltip
                                contentClassName="tooltip-content"
                                allowClick={false}
                                allowHover
                                id={`tooltip`}
                                placement="auto"
                                trigger={
                                  <span aria-label="Tooltip">
                                    {truncateString(sku.Name, 25)}
                                  </span>
                                }
                                usePortal
                                variant="default"
                                hide={sku.Name.length <= 25}
                              >
                                {sku.Name}
                              </MotifTooltip>
                            </div>
                            <div className="light-color">{sku.SkuId}</div>
                          </div>
                        </li>
                        <ul>
                          {sku.SkuRelation.map((skuRelation: any) => {
                            return (
                              <li>
                                <div className="table-cell">
                                  <div>
                                    <MotifTooltip
                                      contentClassName="tooltip-content"
                                      allowClick={false}
                                      allowHover
                                      id={`tooltip`}
                                      placement="auto"
                                      trigger={
                                        <span aria-label="Tooltip">
                                          {truncateString(skuRelation.Name, 25)}
                                        </span>
                                      }
                                      usePortal
                                      variant="default"
                                      hide={skuRelation.Name.length <= 25}
                                    >
                                      {skuRelation.Name}
                                    </MotifTooltip>
                                  </div>
                                  <div className="light-color">
                                    {skuRelation.SkuId}
                                  </div>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </ul>
                    </td>
                    <td className="qty-td">
                      <div>
                        {sku.CostIntervalCd == "onetime" ? (
                          <span>01</span>
                        ) : (
                          <span>{sku.OrderQuantity}</span>
                        )}
                      </div>
                      {sku.SkuRelation.map((skuRelation: any) => {
                        return (
                          <div
                            className="table-cell-child"
                            key={skuRelation.SkuId}
                          >
                            {skuRelation.CostIntervalCd == "onetime" ? (
                              <span>01</span>
                            ) : (
                              <span>{skuRelation.OrderQuantity}</span>
                            )}
                          </div>
                        );
                      })}
                    </td>
                    <td className="unit-cost-td">
                      <div>
                        <div>
                          {sku.CostMetricCost.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                          })}
                        </div>
                        <div className="light-color">
                          {getCostInterval(sku.CostIntervalCd)}
                        </div>
                      </div>
                      {sku.SkuRelation.map((skuRelation: any) => {
                        return (
                          <div
                            key={skuRelation.SkuId}
                            className="table-cell-child duration-div"
                          >
                            {skuRelation.CostMetricCost.toLocaleString(
                              "en-US",
                              {
                                style: "currency",
                                currency: "USD",
                                maximumFractionDigits: 2,
                                minimumFractionDigits: 2,
                              },
                            )}
                            <div className="light-color">
                              {getCostInterval(skuRelation.CostIntervalCd)}
                            </div>
                          </div>
                        );
                      })}
                    </td>
                    <td className="duration-td">
                      <div>{getDuration(sku)}</div>
                      {sku.SkuRelation.map((skuRelation: any) => {
                        return (
                          <div
                            className="table-cell-child duration-div"
                            key={skuRelation.SkuId}
                          >
                            {getDuration(skuRelation)}
                          </div>
                        );
                      })}
                    </td>
                    <td className="subtotal-td">
                      {calculateTotalCost(sku)}
                      {sku.SkuRelation.map((skuRelation: any) => {
                        return (
                          <div
                            className="table-cell-child"
                            key={skuRelation.SkuId}
                          >
                            {calculateTotalCost(skuRelation)}
                          </div>
                        );
                      })}
                    </td>
                  </tr>
                </>
              )}
            </>
          );
        }

        if (sku.SkuRelation.length === 0) {
          return (
            <>
              <tr key={index}>
                <td className="sku-cell">
                  <div className="sku-title">
                    <MotifTooltip
                      contentClassName="tooltip-content"
                      allowClick={false}
                      allowHover
                      id={`tooltip`}
                      placement="auto"
                      trigger={
                        <span aria-label="Tooltip">
                          {truncateString(sku.Name, 25)}
                        </span>
                      }
                      usePortal
                      variant="default"
                      hide={sku.Name.length <= 25}
                    >
                      {sku.Name}
                    </MotifTooltip>
                  </div>
                  <div className="sku-number">{sku.SkuId}</div>
                </td>
                <td className="qty-td">
                  <div className="table-cell">
                    {sku.CostIntervalCd == "onetime" ? (
                      <span>01</span>
                    ) : (
                      <span>{sku.OrderQuantity}</span>
                    )}
                  </div>
                </td>
                <td className="unit-cost-td">
                  <div>
                    {sku.CostMetricCost.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    })}
                  </div>
                  <div className="light-color">
                    {getCostInterval(sku.CostIntervalCd)}
                  </div>
                </td>

                <td className="duration-td">
                  <div>{getDuration(sku)}</div>
                </td>
                <td className="subtotal-td">{calculateTotalCost(sku)}</td>
              </tr>
            </>
          );
        } else {
          return (
            <>
              <tr key={index} style={{ verticalAlign: "baseline" }}>
                <td className="sku-cell">
                  <ul className="tree">
                    <li className="tree-trigger">
                      <div className="table-cell">
                        <div>{sku.Name}</div>
                        <div>{sku.SkuId}</div>
                      </div>
                    </li>
                    <ul>
                      {sku.SkuRelation.map((skuRelation: any) => {
                        return (
                          <li>
                            <div className="table-cell">
                              <div>
                                <MotifTooltip
                                  contentClassName="tooltip-content"
                                  allowClick={false}
                                  allowHover
                                  id={`tooltip`}
                                  placement="auto"
                                  trigger={
                                    <span aria-label="Tooltip">
                                      {truncateString(skuRelation.Name, 25)}
                                    </span>
                                  }
                                  hide={skuRelation.Name.length <= 25}
                                >
                                  {skuRelation.Name}
                                </MotifTooltip>
                              </div>
                              <div>{skuRelation.SkuId}</div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </ul>
                </td>
                <td className="qty-td">
                  <div>
                    {sku.CostIntervalCd == "onetime" ? (
                      <span>01</span>
                    ) : (
                      <span>{sku.OrderQuantity}</span>
                    )}
                  </div>
                  {sku.SkuRelation.map((skuRelation: any) => {
                    return (
                      <div className="table-cell-child" key={skuRelation.SkuId}>
                        {skuRelation.CostIntervalCd == "onetime" ? (
                          <span>01</span>
                        ) : (
                          <span>{skuRelation.OrderQuantity}</span>
                        )}
                      </div>
                    );
                  })}
                </td>
                <td className="unit-cost-td">
                  <div>
                    {sku.CostMetricCost.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    })}
                  </div>
                  <div className="light-color">
                    {getCostInterval(sku.CostIntervalCd)}
                  </div>
                  {sku.SkuRelation.map((skuRelation: any) => {
                    return (
                      <div
                        key={skuRelation.SkuId}
                        className="table-cell-child duration-div"
                      >
                        {skuRelation.CostMetricCost.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 2,
                        })}
                        <div className="light-color">
                          {getCostInterval(skuRelation.CostIntervalCd)}
                        </div>
                      </div>
                    );
                  })}
                </td>
                <td className="duration-td">
                  <div>{getDuration(sku)}</div>
                  {sku.SkuRelation.map((skuRelation: any) => {
                    return (
                      <div
                        className="table-cell-child duration-div"
                        key={skuRelation.SkuId}
                      >
                        {getDuration(skuRelation)}
                      </div>
                    );
                  })}
                </td>
                <td className="subtotal-td">
                  {calculateTotalCost(sku)}
                  {sku.SkuRelation.map((skuRelation: any) => {
                    return (
                      <div className="table-cell-child" key={skuRelation.SkuId}>
                        {calculateTotalCost(skuRelation)}
                      </div>
                    );
                  })}
                </td>
              </tr>
            </>
          );
        }
      })}
    </>
  );
};

export default BundleCard;
